import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ARENA_EVENT_ICON, ARENA_LIVES, ARENA_MINUTES, arenaEventAt, MATCH_VARIANTS, TEAM_ONLY_VARIANTS, type ArenaLives, type ArenaMinutes, type MatchVariant, type QueueMode, type TierId } from '@snakefeast/shared';
import { AdSlot } from '../components/AdSlot';
import { AuthModal } from '../components/AuthForm';
import { GetApp } from '../components/GetApp';
import { LoginStreakCard } from '../components/LoginStreakCard';
import { MissionsCard } from '../components/MissionsCard';
import { PartyCard } from '../components/PartyCard';
import { Tip } from '../components/Tip';
import { Avatar, Coins, Modal, Segmented, TierBadge } from '../components/ui';
import { api } from '../lib/api';
import { lobbySocket } from '../lib/lobbySocket';
import { hasAutoPlay, takeAutoPlay } from '../lib/autoplay';
import { instantMultiplayer, PORTAL, portalAccounts, portalSignIn } from '../lib/portal';
import { sfx, unlockAudio } from '../lib/sound';
import { useLobby } from '../store/lobby';
import { useSession } from '../store/session';
import { useSettings } from '../store/settings';
import { toast } from '../store/toasts';
import { GameIcon, Glyph } from '../components/GameIcon';

export const VARIANT_ICON: Record<MatchVariant, string> = { endless: '🌍', classic: '🐍', powerups: '⚡', royale: '🔥', koth: '👑', ctf: '🏆' };

const MODES: Array<{ id: QueueMode; icon: string }> = [
  { id: 'arena', icon: '🌍' },
  { id: 'duel', icon: '⚔️' },
  { id: 'teams2', icon: '👥' },
  { id: 'koth4', icon: '👑' },
  { id: 'ffa4', icon: '🌀' },
  { id: 'ffa6', icon: '🔥' },
];

interface SeasonPeek {
  tier: TierId;
  division: 1 | 2 | 3 | null;
  points: number;
  progress: number;
  pass: { level: number; xp: number; xpPerLevel: number };
}

/** Tier + battle-pass level at a glance; opens the season screen. */
function SeasonCard() {
  const { t } = useTranslation();
  const nav = useNavigate();
  // undefined while loading, null if it failed (the card is then left out).
  const [s, setS] = useState<SeasonPeek | null | undefined>(undefined);
  useEffect(() => {
    void api<SeasonPeek>('/api/season').then(setS).catch(() => setS(null));
  }, []);
  if (s === null) return null;
  if (!s) {
    return (
      <div className="card season-card" aria-busy="true" aria-label={t('season.cardHint')}>
        <div className="row">
          <div className="skeleton" style={{ width: 92, height: 22, borderRadius: 999 }} />
          <div className="skeleton" style={{ width: 40, height: 12 }} />
          <span className="grow" />
          <div className="skeleton" style={{ width: 62, height: 22, borderRadius: 999 }} />
        </div>
        <div className="skeleton mt" style={{ height: 6, borderRadius: 999 }} />
        <div className="skeleton" style={{ width: 170, height: 12, marginTop: 8 }} />
      </div>
    );
  }
  const passPct = s.pass.level >= 30 ? 100 : ((s.pass.xp % s.pass.xpPerLevel) / s.pass.xpPerLevel) * 100;
  return (
    <button className="card interactive season-card" onClick={() => nav('/season')}>
      <div className="row">
        <TierBadge tier={s.tier} division={s.division} />
        <span className="faint grow">{s.points} RP</span>
        <span className="pill accent">{t('pass.level', { level: s.pass.level })}</span>
      </div>
      <div className="mission-bar mt" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(passPct)}>
        <i style={{ width: `${passPct}%` }} />
      </div>
      <div className="faint" style={{ marginTop: 6 }}>{t('season.cardHint')}</div>
    </button>
  );
}

/** Games before the full home screen (shop, clans, tournaments, season) appears. */
const NEWCOMER_GAMES = 3;

/** The lives choice: a heart per life. */
function Hearts({ n }: { n: number }) {
  return (
    <span className="seg-hearts">
      {Array.from({ length: n }, (_, i) => <GameIcon key={i} name="heart" size="1.05em" />)}
    </span>
  );
}

const SHORTCUTS = [
  { to: '/shop', icon: '🛒', key: 'shop' },
  { to: '/clans', icon: '🏰', key: 'clans' },
  { to: '/tournaments', icon: '🏆', key: 'tournaments' },
  { to: '/live', icon: '📺', key: 'live' },
] as const;

function QueueOverlay() {
  const { t } = useTranslation();
  const queue = useLobby((s) => s.queue);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!queue) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [queue]);
  if (!queue) return null;
  const secs = Math.max(0, Math.floor((now - queue.since) / 1000));
  return (
    <Modal onClose={() => lobbySocket.send({ t: 'queue.leave' })}>
      <div className="center">
        <div className="queue-pulse" aria-hidden="true">
          <span />
          <span />
          <span />
          <div className="core"><GameIcon name="snake" size={36} /></div>
        </div>
        <h2>{t('queue.searching')}</h2>
        <p className="muted">
          {t(`modes.${queue.mode}`)} · {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}
        </p>
        {queue.typicalWaitMs !== null && secs * 1000 < queue.typicalWaitMs * 2 && (
          <p className="faint">{t('queue.typicalWait', { secs: Math.max(1, Math.round(queue.typicalWaitMs / 1000)) })}</p>
        )}
        {secs >= 10 && <p className="faint">{t('queue.widening')}</p>}
        <Tip rotate />
        <button className="btn block mt" onClick={() => lobbySocket.send({ t: 'queue.leave' })}>{t('queue.cancel')}</button>
      </div>
    </Modal>
  );
}

export function Home() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const user = useSession((s) => s.user)!;
  const connection = useLobby((s) => s.connection);
  const room = useLobby((s) => s.room);
  const party = useLobby((s) => s.party);
  const [picker, setPicker] = useState(false);
  const [roomSheet, setRoomSheet] = useState<null | 'create' | 'join'>(null);
  const [roomMode, setRoomMode] = useState<'ffa' | 'teams'>('ffa');
  const [roomSize, setRoomSize] = useState(4);
  const [roomVariant, setRoomVariant] = useState<MatchVariant>('endless');
  const [practicePicker, setPracticePicker] = useState(false);
  // New players start practice on easy bots.
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>(() => (user.stats.gamesPlayed + user.stats.casualGames < 10 ? 'easy' : 'medium'));
  const [arenaMinutes, setArenaMinutes] = useState<ArenaMinutes>(0);
  const [arenaLives, setArenaLives] = useState<ArenaLives>(3);
  const livesPicker = (
    <div className="field" style={{ marginBottom: 6 }}>
      <label>{t('modes.lives')}</label>
      <Segmented
        value={String(arenaLives)}
        onChange={(v) => setArenaLives(Number(v) as ArenaLives)}
        options={ARENA_LIVES.map((n) => ({ value: String(n), label: <span className="seg-icon"><Hearts n={n} /> {n === 3 ? t('modes.threeHearts') : t('modes.oneLife')}</span> }))}
      />
    </div>
  );
  const [code, setCode] = useState('');
  const [upgrade, setUpgrade] = useState(false);
  const settings = useSettings();
  const teamOnly = TEAM_ONLY_VARIANTS.includes(roomVariant);

  useEffect(() => {
    if (room) nav('/room');
  }, [room, nav]);

  // In a friend's party the leader picks the game for everyone.
  const partyFollower = !!party && party.members.length > 1 && party.leaderId !== user.id;
  const queue = (mode: QueueMode) => {
    unlockAudio();
    sfx('click');
    if (partyFollower) {
      toast(t('party.memberHint'), 'info');
      return;
    }
    // A brand-new player's first "Play" is the guided tutorial match (not when a portal sends them straight to multiplayer).
    if (mode === 'arena' && !settings.tutorialDone && user.stats.gamesPlayed + user.stats.casualGames === 0 && !party && !instantMultiplayer()) {
      nav('/practice?tutorial=1');
      return;
    }
    setPicker(false);
    lobbySocket.send(mode === 'arena' ? { t: 'queue.join', mode, arenaMinutes, arenaLives } : { t: 'queue.join', mode });
  };

  const s = user.stats;
  const played = s.gamesPlayed + s.casualGames;

  // Arrived from the landing page's "Play now": start the game straight away. The tutorial runs
  // offline, so it needn't wait for the lobby connection; the arena does.
  const tutorialNext = !settings.tutorialDone && played === 0 && !party && !instantMultiplayer();
  useEffect(() => {
    if (!hasAutoPlay() || (connection !== 'open' && !tutorialNext)) return;
    takeAutoPlay();
    queue('arena');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection]);
  // New players see a short home screen: play first, the extras unlock after a few games.
  const newcomer = played < NEWCOMER_GAMES;
  return (
    <div className="page">
      <div className="hero-head">
        <Avatar avatar={user.avatar} avatarUrl={user.avatarUrl} name={user.displayName} size={56} premium={user.isPremium} />
        <div className="grow">
          <div className="faint">{t('app.name')}</div>
          <h1 className="truncate">{t('home.greeting', { name: user.displayName })}</h1>
        </div>
        <button className="btn ghost small" onClick={() => nav('/shop')} aria-label={t('nav.shop')} data-coin-target><Coins value={user.coins} /></button>
        {user.isPremium && <span className="pill premium"><Glyph e="👑" /> {t('common.premium')}</span>}
      </div>

      {connection !== 'open' && <div className="card tight muted" style={{ marginBottom: 14 }}>{navigator.onLine ? t('home.connecting') : t('home.offline')}</div>}

      <button className="play-now" onClick={() => queue('arena')} disabled={connection !== 'open'}>
        <span className="play-now-icon" aria-hidden="true">▶</span>
        <span className="grow">
          <strong>{t('home.playNow')}</strong>
          <small>{t('home.playNowDesc')}</small>
        </span>
      </button>

      <ArenaEventStrip onPlay={() => queue('arena')} disabled={connection !== 'open'} />

      <PartyCard />

      <div className="quick-row">
        <button className="card tight interactive quick" onClick={() => { unlockAudio(); setPicker(true); }} disabled={connection !== 'open'}>
          <GameIcon name="gamepad" size={38} />
          <strong>{t('home.allModes')}</strong>
        </button>
        <button className="card tight interactive quick" onClick={() => setRoomSheet('create')} disabled={connection !== 'open'}>
          <GameIcon name="users" size={38} />
          <strong>{t('home.withFriends')}</strong>
        </button>
        <button className="card tight interactive quick" onClick={() => setRoomSheet('join')} disabled={connection !== 'open'}>
          <GameIcon name="key" size={38} />
          <strong>{t('home.joinRoom')}</strong>
        </button>
        <button className="card tight interactive quick" onClick={() => { unlockAudio(); nav('/solo'); }}>
          <GameIcon name="trophy" size={38} />
          <strong>{t('solo.homeTitle')}</strong>
        </button>
        <button className="card tight interactive quick" onClick={() => { unlockAudio(); setPracticePicker(true); }}>
          <GameIcon name="robot" size={38} />
          <strong>{t('home.practice')}</strong>
        </button>
        <button className="card tight interactive quick" onClick={() => { sfx('click'); nav('/snake'); }}>
          <GameIcon name="palette" size={38} />
          <strong>{t('mySnake.title')}</strong>
        </button>
      </div>

      {!settings.dismissedGetApp && (
        <div style={{ marginTop: 12 }}>
          <GetApp onDismiss={() => settings.set({ dismissedGetApp: true })} />
        </div>
      )}

      {/* On a portal the only sign-in is the portal's own (its rules forbid others): saves progress the same way. */}
      {user.isGuest && (!PORTAL || portalAccounts()) && (
        <button className="card tight interactive row" style={{ marginTop: 12 }} onClick={() => (PORTAL ? portalSignIn() : setUpgrade(true))}>
          <GameIcon name="cloud" size={30} />
          <div className="grow">
            <strong>{t('auth.upgradeTitle')}</strong>
            <div className="faint">{t('auth.upgradeBody')}</div>
          </div>
        </button>
      )}

      {newcomer ? (
        <div className="card tight mt unlock-hint">
          <GameIcon name="unlock" size={30} />
          <div className="grow">
            <strong>{t('home.unlockTitle')}</strong>
            <div className="faint">{t('home.unlockBody', { count: Math.max(0, NEWCOMER_GAMES - played) })}</div>
            <div className="mission-bar"><i style={{ width: `${(played / NEWCOMER_GAMES) * 100}%` }} /></div>
          </div>
        </div>
      ) : (
        <>
          <div className="card mt">
            <div className="stats-row">
              <div className="stat"><span className="v">{s.rating}</span><span className="k">{t('home.rating')}</span></div>
              <div className="stat"><span className="v">{s.gamesPlayed ? Math.round(s.winRate * 100) : 0}%</span><span className="k">{t('home.winRate')}</span></div>
              <div className="stat"><span className="v">{s.currentStreak}<GameIcon name="flame" size="0.85em" /></span><span className="k">{t('home.streak')}</span></div>
            </div>
          </div>
          <div className="shortcuts mt">
            {SHORTCUTS.map((sc) => (
              <button key={sc.to} className="card tight interactive shortcut" onClick={() => nav(sc.to)}>
                <span className="shortcut-icon"><Glyph e={sc.icon} size={30} /></span>
                <strong>{t(`nav.${sc.key}`)}</strong>
              </button>
            ))}
          </div>
          <div className="grid two mt">
            <SeasonCard />
          </div>
        </>
      )}

      <LoginStreakCard />
      <MissionsCard />

      <div className="mt">
        <AdSlot />
      </div>

      {picker && (
        <Modal title={t('home.quickPlay')} onClose={() => setPicker(false)}>
          <div className="stack">
            {MODES.map((m) => (
              <div key={m.id} className="stack" style={{ gap: 6 }}>
                <button className="card tight interactive mode-card" onClick={() => queue(m.id)}>
                  <div className="icon"><Glyph e={m.icon} size={30} /></div>
                  <div className="grow">
                    <strong>{t(`modes.${m.id}`)}</strong>
                    <div className="faint">{t(`modes.${m.id}Desc`)}</div>
                  </div>
                </button>
                {m.id === 'arena' && livesPicker}
                {m.id === 'arena' && (
                  <div className="field" style={{ marginBottom: 6 }}>
                    <label>{t('modes.arenaTime')}</label>
                    <Segmented
                      value={String(arenaMinutes)}
                      onChange={(v) => setArenaMinutes(Number(v) as ArenaMinutes)}
                      options={ARENA_MINUTES.map((n) => ({ value: String(n), label: n ? t('modes.arenaMinutes', { count: n }) : t('modes.arenaNoLimit') }))}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}

      {practicePicker && (
        <Modal title={t('home.practice')} onClose={() => setPracticePicker(false)}>
          <div className="stack">
            <div className="field">
              <label>{t('home.difficulty')}</label>
              <Segmented
                value={difficulty}
                onChange={setDifficulty}
                options={(['easy', 'medium', 'hard'] as const).map((d) => ({ value: d, label: t(`home.difficulties.${d}`) }))}
              />
            </div>
            {livesPicker}
            {MATCH_VARIANTS.map((v) => (
              <button key={v} className="card tight interactive mode-card" onClick={() => { sfx('click'); nav(`/practice?v=${v}&d=${difficulty}${v === 'endless' ? `&l=${arenaLives}` : ''}`); }}>
                <div className="icon"><Glyph e={VARIANT_ICON[v]} size={30} /></div>
                <div className="grow">
                  <strong>{t(`variants.${v}.name`)}</strong>
                  <div className="faint">{t(`variants.${v}.desc`)}</div>
                </div>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {roomSheet === 'create' && (
        <Modal title={t('room.create')} onClose={() => setRoomSheet(null)}>
          <div className="stack">
            <div className="field">
              <label>{t('room.mode')}</label>
              <Segmented value={teamOnly ? 'teams' : roomMode} onChange={(v) => { setRoomMode(v); if (v === 'teams' && roomSize % 2) setRoomSize(4); }} options={teamOnly ? [{ value: 'teams', label: t('modes.teams') }] : [{ value: 'ffa', label: t('modes.ffa') }, { value: 'teams', label: t('modes.teams') }]} />
            </div>
            <div className="field">
              <label>{t('room.rules')}</label>
              <Segmented
                grid
                value={roomVariant}
                onChange={(v) => {
                  setRoomVariant(v);
                  if (TEAM_ONLY_VARIANTS.includes(v) && roomSize % 2) setRoomSize(4);
                }}
                options={MATCH_VARIANTS.map((v) => ({ value: v, label: <span className="seg-icon"><Glyph e={VARIANT_ICON[v]} /> {t(`variants.${v}.name`)}</span> }))}
              />
            </div>
            <div className="field">
              <label>{t('room.size')}</label>
              <Segmented
                value={String(roomSize)}
                onChange={(v) => setRoomSize(Number(v))}
                options={(roomMode === 'teams' || teamOnly ? [2, 4, 6, 8] : [2, 3, 4, 6, 8]).map((n) => ({ value: String(n), label: String(n) }))}
              />
            </div>
            <button className="btn primary lg block" onClick={() => { lobbySocket.send({ t: 'room.create', mode: teamOnly ? 'teams' : roomMode, maxPlayers: roomSize, variant: roomVariant }); setRoomSheet(null); }}>
              {t('room.create')}
            </button>
          </div>
        </Modal>
      )}

      {roomSheet === 'join' && (
        <Modal title={t('room.joinTitle')} onClose={() => setRoomSheet(null)}>
          <form className="stack" onSubmit={(e) => { e.preventDefault(); lobbySocket.send({ t: 'room.join', code }); setRoomSheet(null); }}>
            <input className="input code" maxLength={6} autoFocus placeholder={t('room.codePlaceholder')} value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} aria-label={t('room.code')} />
            <button className="btn primary lg block" disabled={code.length < 6}>{t('room.join')}</button>
          </form>
        </Modal>
      )}

      {upgrade && (
        <AuthModal registerTitle={t('auth.upgradeTitle')} intro={t('auth.upgradeBody')} onClose={() => setUpgrade(false)} />
      )}

      <QueueOverlay />
    </div>
  );
}

/** This week's big-arena event, under Play now (tapping it plays too). */
function ArenaEventStrip({ onPlay, disabled }: { onPlay: () => void; disabled: boolean }) {
  const { t, i18n } = useTranslation();
  const { id, endsAt } = arenaEventAt(Date.now());
  const left = endsAt - Date.now();
  const days = Math.floor(left / 86_400_000);
  const time = new Intl.NumberFormat(i18n.language, { style: 'unit', unit: days >= 1 ? 'day' : 'hour', unitDisplay: 'long' }).format(
    days >= 1 ? days : Math.max(1, Math.floor(left / 3_600_000)),
  );
  return (
    <button className="event-strip" onClick={onPlay} disabled={disabled}>
      <span className="event-strip-icon"><Glyph e={ARENA_EVENT_ICON[id]} size={32} /></span>
      <span className="grow">
        <small>{t('events.title')} · {t('events.endsIn', { time })}</small>
        <strong>{t(`events.${id}`)}</strong>
        <span className="faint">{t(`events.${id}Desc`)}</span>
      </span>
    </button>
  );
}
